import { useState } from "react";
import { toast } from "sonner";
import { api, extractApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import {
  Scales, Calculator, Bank as BankIcon, ShieldCheck, Truck, Cpu, Buildings,
  ArrowRight, CheckCircle, Sparkle, ArrowUpRight,
} from "@phosphor-icons/react";

const CATEGORIES = [
  { key: "legal",       label: "Legal Partners",   icon: Scales,     tagline: "Cross-border corporate & regulatory counsel" },
  { key: "tax",         label: "Tax Partners",     icon: Calculator, tagline: "Tax structuring & compliance across EM jurisdictions" },
  { key: "banks",       label: "Banks",            icon: BankIcon,   tagline: "Correspondent banking & trade finance networks" },
  { key: "dfis",        label: "DFIs",             icon: ShieldCheck,tagline: "Development finance institutions for co-investment" },
  { key: "insurance",   label: "Insurance",        icon: ShieldCheck,tagline: "Political-risk, trade credit & surety insurers" },
  { key: "logistics",   label: "Logistics",        icon: Truck,      tagline: "Freight, customs & last-mile execution partners" },
  { key: "technology",  label: "Technology",       icon: Cpu,        tagline: "Regional tech vendors & institutional platforms" },
];

export default function Marketplace() {
  const [selected, setSelected] = useState([]);
  const [form, setForm] = useState({ email: "", organization: "", role: "" });
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const toggle = (key) => {
    setSelected((s) => s.includes(key) ? s.filter((x) => x !== key) : [...s, key]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) { toast.error("Please enter your work email."); return; }
    setBusy(true);
    try {
      await api.post("/marketplace/notify", {
        email: form.email.trim().toLowerCase(),
        categories: selected,
        organization: form.organization || null,
        role: form.role || null,
      });
      setSubmitted(true);
      toast.success("You are on the waitlist. We will notify you at launch.");
    } catch (e) {
      toast.error(extractApiError(e, "Could not add you to the waitlist."));
    }
    setBusy(false);
  };

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-7xl mx-auto" data-testid="marketplace-page">
      <PageHeader
        eyebrow="Institutional network"
        title="Partner Marketplace"
        subtitle="A curated directory of institutional partners across seven categories — from cross-border counsel to development finance institutions. Currently in private build."
      />

      <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold-soft text-gold px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em]" data-testid="marketplace-coming-soon">
        <Sparkle size={12} weight="fill" /> Coming soon · Q3 2026
      </div>

      {/* Categories grid */}
      <div className="mt-10">
        <div className="text-overline text-muted-foreground mb-4">Seven partner categories</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {CATEGORIES.map((c) => {
            const active = selected.includes(c.key);
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => toggle(c.key)}
                className={`text-left rounded-2xl border p-5 transition-all card-elev ${active ? "border-primary bg-primary/5 ring-2 ring-primary/20" : "border-border bg-card hover:-translate-y-0.5"}`}
                data-testid={`marketplace-cat-${c.key}`}
                aria-pressed={active}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className={`h-10 w-10 rounded-lg grid place-items-center ${active ? "bg-primary text-primary-foreground" : "bg-teal-soft text-teal"}`}>
                    <c.icon size={20} weight="duotone" />
                  </div>
                  {active && <CheckCircle size={16} weight="fill" className="text-primary" />}
                </div>
                <div className="mt-4 font-display text-lg tracking-tight">{c.label}</div>
                <div className="mt-1 text-xs text-muted-foreground leading-relaxed">{c.tagline}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Notify me form */}
      <div className="mt-12 bg-primary text-primary-foreground rounded-2xl p-6 sm:p-10 card-elev">
        {submitted ? (
          <div className="text-center py-4" data-testid="marketplace-submitted">
            <div className="mx-auto h-12 w-12 rounded-full bg-teal-soft/20 text-teal grid place-items-center mb-4">
              <CheckCircle size={22} weight="fill" />
            </div>
            <div className="font-display text-2xl tracking-tight">You are on the list.</div>
            <p className="mt-3 text-sm opacity-90 max-w-md mx-auto">We will notify <span className="font-semibold">{form.email}</span> as soon as the Partner Marketplace opens for early access.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr,1fr] gap-6 lg:gap-10 items-center">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-80 mb-3">Early access</div>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight">Notify me when the Marketplace launches.</h2>
              <p className="mt-4 text-sm opacity-90 max-w-md leading-relaxed">
                Get priority access to our curated network. Select the categories you care about above — we will invite you first when partners in those categories go live.
              </p>
            </div>
            <form onSubmit={submit} className="space-y-3 bg-background text-foreground rounded-xl p-5 sm:p-6" data-testid="marketplace-form">
              <div>
                <label className="text-overline text-muted-foreground">Work email *</label>
                <input
                  type="email" required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1 w-full h-10 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  placeholder="jane.doe@yourfund.com"
                  data-testid="marketplace-email"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-overline text-muted-foreground">Organisation</label>
                  <input
                    type="text"
                    value={form.organization}
                    onChange={(e) => setForm({ ...form, organization: e.target.value })}
                    className="mt-1 w-full h-10 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="Fund / Firm"
                    data-testid="marketplace-org"
                  />
                </div>
                <div>
                  <label className="text-overline text-muted-foreground">Role</label>
                  <input
                    type="text"
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    className="mt-1 w-full h-10 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="Partner · Director · Analyst"
                    data-testid="marketplace-role"
                  />
                </div>
              </div>
              {selected.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  Interested in: <span className="font-semibold text-foreground">{selected.length} categor{selected.length === 1 ? "y" : "ies"}</span>
                </div>
              )}
              <button
                type="submit"
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-lg px-5 py-3 text-sm font-semibold hover:opacity-90 disabled:opacity-50 card-elev"
                data-testid="marketplace-submit"
              >
                {busy ? "Adding you…" : "Notify me at launch"} <ArrowRight size={14} weight="bold" />
              </button>
            </form>
          </div>
        )}
      </div>

      {/* Sub-note */}
      <p className="mt-6 text-xs text-muted-foreground text-center max-w-2xl mx-auto leading-relaxed">
        Are you a partner interested in listing? <a href="mailto:partners@orixa-invest.co" className="text-primary underline" data-testid="marketplace-partner-email">partners@orixa-invest.co</a>
      </p>
    </div>
  );
}
